import { motion } from "framer-motion";
import { Baby, Briefcase, PersonStanding, Trophy, ArrowUpRight } from "lucide-react";

const groups = [
  {
    n: "01",
    icon: Baby,
    title: "Busy Parents",
    body: "Strength and energy that fits around school runs and sleepless nights. Efficient one-to-one sessions at times that actually work for family life.",
    goal: "Energy that lasts all day",
  },
  {
    n: "02",
    icon: Briefcase,
    title: "Hard Workers",
    body: "Desk-bound weeks take their toll. Undo the sitting, fix the aches, and build a body that keeps up with your ambition — without living in the gym.",
    goal: "Strong, pain-free, sharper",
  },
  {
    n: "03",
    icon: PersonStanding,
    title: "The Older Generation",
    body: "It's never too late to get stronger. Safe, patient coaching focused on balance, bone health and the confidence to keep doing everything you love.",
    goal: "Move with confidence",
  },
  {
    n: "04",
    icon: Trophy,
    title: "Athletes",
    body: "Structured strength, speed and power work with everything measured. Programmed to peak when it counts — in-season and off.",
    goal: "Perform on demand",
  },
];

export default function WhoWeHelp() {
  return (
    <section className="py-24 md:py-40 border-b border-white/10" data-testid="who-we-help-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <h2 className="font-display font-bold uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl" data-testid="who-we-help-heading">
            Who I <span className="text-gold italic">Help</span>
          </h2>
          <p className="max-w-sm text-zinc-400 text-base md:text-lg">
            Different lives, different goals. One coach, and a programme built around yours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {groups.map((g, i) => (
            <motion.div
              key={g.n}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="group relative border border-white/10 bg-secondary/40 p-8 flex flex-col hover:border-gold/60 transition-colors duration-500"
              data-testid={`who-we-help-card-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-start justify-between mb-10">
                <g.icon className="w-8 h-8 text-gold" />
                <span className="font-display font-bold text-stroke-gold text-4xl select-none" aria-hidden="true">{g.n}</span>
              </div>
              <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight mb-3">{g.title}</h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed flex-1">{g.body}</p>
              <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
                <p className="font-display uppercase tracking-[0.15em] text-xs text-gold">{g.goal}</p>
                <a
                  href="#contact"
                  data-testid={`who-we-help-cta-${g.n}`}
                  aria-label={`Book a consultation — ${g.title}`}
                  className="text-zinc-500 hover:text-gold transition-colors duration-300"
                >
                  <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
